import { getPicklistValues } from '../../config/picklistConfig';
import { CountyAreasValues } from '../../components/CountyAreasFields';

// Multipicklists the page edits; the API carries Nexus values, the form pickers carry portal labels.
export const AVAILABILITY_FIELDS = [
  'availableDays',
  'availabilityPreferences',
  'areasOfWorkMiamiDade',
  'areasOfWorkBroward',
  'areasOfWorkPalmBeach',
] as const;

export type AvailabilityField = (typeof AVAILABILITY_FIELDS)[number];
export type ApiAvailability = Record<AvailabilityField, string[]>;

const COUNTY_AREAS = [
  ['countyMiamiDade', 'areasOfWorkMiamiDade'],
  ['countyBroward', 'areasOfWorkBroward'],
  ['countyPalmBeach', 'areasOfWorkPalmBeach'],
] as const;

export interface AvailabilityFormData extends CountyAreasValues {
  availableDays: string;
  availabilityPreferences: string;
}

export const EMPTY_FORM: AvailabilityFormData = {
  availableDays: '',
  availabilityPreferences: '',
  countyMiamiDade: false,
  countyBroward: false,
  countyPalmBeach: false,
  areasOfWorkMiamiDade: '',
  areasOfWorkBroward: '',
  areasOfWorkPalmBeach: '',
};

const toApiValue = (label: string) => label.trim().toUpperCase().replace(/\s+/g, '_');
const splitLabels = (value: string) => value.split(';').filter(Boolean);

/** Nexus API values → semicolon-separated portal labels, as the caregiver form pickers expect. */
export function fromApi(availability: Partial<ApiAvailability>): AvailabilityFormData {
  const form = { ...EMPTY_FORM };
  for (const field of AVAILABILITY_FIELDS) {
    const labels = getPicklistValues('caregiver', field);
    form[field] = (availability[field] ?? [])
      .map((value) => labels.find((label) => toApiValue(label) === value))
      .filter(Boolean)
      .join(';');
  }
  for (const [county, areas] of COUNTY_AREAS) {
    form[county] = form[areas] !== '';
  }
  return form;
}

/** Portal labels → Nexus API values; an unchecked county clears its areas. */
export function toApi(form: AvailabilityFormData): ApiAvailability {
  const availability = {} as ApiAvailability;
  for (const field of AVAILABILITY_FIELDS) {
    availability[field] = splitLabels(form[field]).map(toApiValue);
  }
  for (const [county, areas] of COUNTY_AREAS) {
    if (!form[county]) availability[areas] = [];
  }
  return availability;
}

/** Same rules as the caregiver form's availability step; values are i18n keys. */
export function validateAvailability(form: AvailabilityFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (splitLabels(form.availableDays).length === 0) errors.availableDays = 'availability.selectDay';
  if (splitLabels(form.availabilityPreferences).length === 0) errors.availabilityPreferences = 'availability.selectShift';
  if (COUNTY_AREAS.every(([county]) => !form[county])) errors.countySelection = 'availability.selectCounty';
  for (const [county, areas] of COUNTY_AREAS) {
    if (form[county] && splitLabels(form[areas]).length < 2) errors[areas] = 'availability.selectAreas';
  }
  return errors;
}
